/**
 * PDF preview panel of the Final Documents screen.
 *
 * Renders the submission packet with the oasis {@link PdfRenderer} and the
 * shared {@link OasisPdfToolbar} (search + zoom + rotate), matching the
 * Medical Necessity viewer. The toolbar's leading slot holds the
 * {@link DocumentMappingList} index built from `document_page_mapping`, which
 * jumps the viewer to a source document's first page. That index is omitted
 * when `documentPageMapping` is empty or unset (fax flow previews the filled
 * form, whose pages do not match the clinical mapping). The merged packet is
 * preferred when available; otherwise the first attachment is shown.
 * jumps the viewer to a source document's first page. The merged packet is
 * preferred when available; otherwise the first attachment is shown. The oasis
 * toolbar includes thumbs-up / thumbs-down so reviewers can approve or reject
 * the packet (thumbs-down opens a comment modal).
 *
 * The oasis renderer exposes no rotate API, so page rotation is owned here:
 * the angle is kept in local state and applied to the rendered
 * `.pdf-renderer__page` elements via a scoped `<style>` rule (which also
 * covers pages mounted later as the user scrolls).
 * @module finalDocuments/components/FinalDocumentsPdfPanel
 */
import {
  PdfRenderer,
  PdfRendererHandle,
} from "@risalabs_frontend_org/oasis-ui-kit";
import React, { useRef, useState } from "react";
import DocumentFeedbackModal from "../../../components/documentFeedback/DocumentFeedbackModal";
import { useDocumentFeedback } from "../../../components/documentFeedback/useDocumentFeedback";
import { DocumentMappingList } from "../../../components/documentMappingList/DocumentMappingList";
import { LoaderMessage } from "../../../components/loaderMessage/loaderMessage";
import { OasisPdfToolbar } from "../../../components/oasisPdfToolbar/OasisPdfToolbar";
import { DocumentPageMapping } from "../../../data-model/medicalPaOrdersModel";
import { ScreenNamesForBigQuery } from "../../../enums/eventsData";

/** Selector used to size the document index against this panel. */
const PANEL_SELECTOR = ".final-documents--pdf";

/** Props for {@link FinalDocumentsPdfPanel}. */
export interface FinalDocumentsPdfPanelProps {
  /** Merged submission packet; takes precedence over `documents`. */
  mergedFile?: File | null;
  /** Individual attachments, previewed when there is no merged packet. */
  documents: File[];
  /** Whether the clinical document is still being fetched. */
  isLoading: boolean;
  /** Per-source-document page ranges from `clinical_document.document_page_mapping`. */
  documentPageMapping?: DocumentPageMapping[];
  /** Message shown while loading. */
  loaderMessage?: string;
}

/**
 * Renders the Final Documents PDF viewer panel.
 *
 * @input props - Merged/individual documents, loading state, document page mapping
 * @return The PDF viewer for the submission packet
 */
const FinalDocumentsPdfPanel: React.FC<FinalDocumentsPdfPanelProps> = ({
  mergedFile,
  documents,
  isLoading,
  documentPageMapping,
  loaderMessage = "Fetching clinical document...",
}) => {
  const pdfRef = useRef<PdfRendererHandle>(null);
  const [rotation, setRotation] = useState(0);
  const {
    feedback,
    handleFeedback,
    isFeedbackModalOpen,
    submitNegativeFeedback,
    closeFeedbackModal,
  } = useDocumentFeedback({
    screenName: ScreenNamesForBigQuery.FINAL_DOCUMENT_SCREEN,
  });

  const activeFile = mergedFile ?? documents[0];
  const mappings = documentPageMapping ?? [];

  /** Advances page rotation by 90° (0 → 90 → 180 → 270 → 0). */
  const rotatePage = () => setRotation((prev) => (prev + 90) % 360);

  return (
    <div className="final-documents--pdf flex h-full w-full flex-col overflow-hidden rounded-xl border border-primaryGray-14 bg-white">
      {rotation !== 0 && (
        <style>
          {`.final-documents--pdf .pdf-renderer__page {` +
            ` transform: rotate(${rotation}deg); transform-origin: center center; }`}
        </style>
      )}
      {isLoading ? (
        <div className="flex h-full w-full items-center justify-center">
          <LoaderMessage message={loaderMessage} />
        </div>
      ) : activeFile ? (
        <PdfRenderer
          key={activeFile.name + activeFile.size}
          ref={pdfRef}
          src={activeFile}
          wasmUrl="/pdfium.wasm"
          className="h-full w-full !rounded-none !border-0"
          height="100%"
          width="100%"
          features={{
            zoom: true,
            search: true,
            thumbnails: false,
            pageNavigation: true,
          }}
          toolbar={(ctx) => (
            <OasisPdfToolbar
              ctx={ctx}
              rotation={rotation}
              onRotate={rotatePage}
              showFeedback
              feedback={feedback}
              onFeedback={handleFeedback}
              leadingContent={
                mappings.length > 0 ? (
                  <DocumentMappingList
                    mappings={mappings}
                    onSelectPage={(page) => ctx.goToPage(page)}
                    containerSelector={PANEL_SELECTOR}
                  />
                ) : undefined
              }
            />
          )}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center p-4 text-sm text-primaryGray-10">
          No clinical document available for this order.
        </div>
      )}
      <DocumentFeedbackModal
        isOpen={isFeedbackModalOpen}
        onClose={closeFeedbackModal}
        onSubmit={submitNegativeFeedback}
      />
    </div>
  );
};

export default FinalDocumentsPdfPanel;
export { FinalDocumentsPdfPanel };
